import axios from "axios";
import * as React from "react";
import { useEffect, useState } from "react";
import i18n from "i18n";
import { WBSStore } from "modules/WBS/WBSStore";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  withStyles,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import { GroupWork } from "@material-ui/icons";
import { CustomPropertyStore } from "modules/customProperties/CustomPropertyStore";
import { alternativeCodeLabel, resourceGroupLabel } from "common/constants";
import { sortString } from "utils/StringUtils";
import { formatNumber } from "utils/MathUtils";

const styles = (theme) => ({
  dialogClass: {
    "& > div:nth-last-child(1)": {
      borderRadius: "8px",
      "& > div": {
        borderRadius: "8px",
        backgroundColor: "#fff",
        height: "calc(100% - 96px)",
        "& > div:nth-last-child(1)": {
          backgroundColor: "#fff",
        },
      },
    },
  },
  dialogTitleClass: {
    backgroundColor: "#fff",
    border: "none",
    margin: "0 0 25px 29px",
    "& h6": {
      whiteSpace: "nowrap" as any,
      textOverflow: "ellipsis" as any,
      overflow: "hidden",
      fontSize: ".9rem",
      fontWeight: 400,
      margin: "1rem 0.5rem",
      color: "#333",
      "& span": {
        marginRight: "5%",
      },
    },
  },
  boxTables: {
    borderRadius: "5px",
    border: "1px solid #E0E0E0",
    padding: "10px 5px",
    marginTop: "25px",
    "&:nth-last-child(1)": {
      marginTop: "30px",
    },
  },
  titleBox: {
    display: "flex",
    flexDirection: "row" as any,
    alignItems: "center",
    marginBottom: "26px",
    "& svg": {
      marginRight: "10px",
    },
    "& h3": {
      fontSize: "16px",
      lineHeight: "19px",
      fontWeight: 500,
      color: "#333",
    },
  },
  iconGreen: {
    color: "#0E8755",
  },
  iconRed: {
    color: "#D73912",
  },
  cellCenter: {
    textAlign: "center" as any,
  },
  cellRight: {
    textAlign: "right" as any,
  },
  cellEmptyCenter: {
    textAlign: "center" as any,
    fontStyle: "italic",
    color: "#757575",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "16px",
    fontFamily: "Roboto",
  },
});

const TableCompositionHead = ({ classes }) => {
  const cellPaddingCorrect = { padding: "4px 24px" };
  return (
    <TableHead>
      <TableCell className={classes.cellCenter} style={cellPaddingCorrect}>
        {i18n.updateCompositions.code}
      </TableCell>
      <TableCell className={classes.cellCenter} style={cellPaddingCorrect}>
        {i18n.updateCompositions.alternativeCode}
      </TableCell>
      <TableCell style={cellPaddingCorrect}>
        {i18n.updateCompositions.description}
      </TableCell>
      <TableCell style={cellPaddingCorrect}>
        {i18n.updateCompositions.resourceGroup}
      </TableCell>
      <TableCell className={classes.cellCenter} style={cellPaddingCorrect}>
        {i18n.updateCompositions.unity}
      </TableCell>
      <TableCell className={classes.cellCenter} style={cellPaddingCorrect}>
        {i18n.updateCompositions.quantity}
      </TableCell>
      <TableCell
        className={classes.cellRight}
        style={{ width: "135px", paddingRight: "24px" }}
      >
        {i18n.updateCompositions.unitCost}
      </TableCell>
      <TableCell className={classes.cellRight} style={{ width: "135px" }}>
        {i18n.updateCompositions.totalCost}
      </TableCell>
    </TableHead>
  );
};

const TableComposition = ({
  classes,
  isNew,
  code,
  alternativeCodeComposition,
  description,
  children,
}) => {
  const title = isNew
    ? i18n.updateCompositions.newComposition
    : i18n.updateCompositions.currentComposition;
  const style = code == undefined ? { color: "#9E9E9E" } : {};

  return (
    <div className={classes.boxTables}>
      <div className={classes.titleBox}>
        <GroupWork className={isNew ? classes.iconGreen : classes.iconRed} />
        <Typography variant="h3">{title}</Typography>
      </div>
      <p className={classes.dialogTitleClass}>
        <h6>
          <span style={style}>{code !== undefined ? code : "0000"}</span>
          {alternativeCodeComposition !== undefined && (
            <span style={style}>{alternativeCodeComposition}</span>
          )}
          {description}
        </h6>
      </p>
      <Table>
        <TableCompositionHead classes={classes} />
        <TableBody>{children}</TableBody>
      </Table>
    </div>
  );
};

const DetailsModal = (props) => {
  const { classes, showModal, setShowModal, rowDataShow } = props;
  const [dataNew, setDataNew] = useState<Array<any>>([]);
  const [dataCurrent, setDataCurrent] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(true);
  const allWBSCustomProperties = CustomPropertyStore.getWBSCustomProperties();

  useEffect(() => {
    setDataNew([]);
    setDataCurrent([]);
    setLoading(true);
    getData();
  }, [rowDataShow]);

  const getData = () => {
    axios({
      method: "POST",
      url: WBSStore.updateCompositionServiceURL + "get-composition-content",
      data: {
        costPlanId: WBSStore.currentWbsId,
        rateLibraryId: WBSStore.CompositionUsedToUpdate,
        rowCode: rowDataShow.code,
      },
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then(function (response) {
        let responseCurrent: Array<any> = [];
        let responseNew: Array<any> = [];
        if (response.data.new) {
          responseNew = response.data.new.composition.map((item) => {
            let list = {
              code: item.code,
              description: item.description,
              unit: item.unity,
              quantity: item.coefficient,
              unitcost: item.unityCost,
              totalCost: item.totalCost,
              alternativeCodeComposition: item.alternativeCodeComposition,
              alternativeCodeItem: item.alternativeCodeItem,
              descriptionGroup: item.descriptionGroup,
            };
            return list;
          });
          responseNew.sort((a, b) => sortString(a.description, b.description));
        }
        if (response.data.current) {
          responseCurrent = response.data.current.compositions.map((item) => {
            let _customProperties = new Map();
            allWBSCustomProperties.map((el) => {
              if (
                el.data.label === alternativeCodeLabel &&
                item.customProperties[el.id] !== undefined &&
                item.customProperties[el.id] !== ""
              ) {
                let key = el.id;
                _customProperties.set(key, item.customProperties[el.id]);
              }
              if (
                el.data.label === resourceGroupLabel &&
                item.customProperties[el.id] !== undefined &&
                item.customProperties[el.id] !== ""
              ) {
                let key = el.id;
                _customProperties.set(key, item.customProperties[el.id]);
              }
            });
            const customProperties = new Object();
            _customProperties.forEach((prop, index) => {
              customProperties[index] = prop;
            });
            let list = {
              code: item.rowCode,
              description: item.description,
              unit: item.unit,
              quantity: item.quantity,
              unitcost: item.unitCost,
              totalCost: item.total,
              customProperties: customProperties,
            };
            return list;
          });
          responseCurrent.sort((a, b) =>
            sortString(a.description, b.description)
          );
        }
        setDataNew(responseNew);
        setDataCurrent(responseCurrent);
        setLoading(false);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const displayTableBody = (news) => {
    const cellPaddingCorrect = { padding: "4px 24px" };
    return news.map((newsItem) => {
      let customAlternativeCode = "";
      let customDescriptionGroup = "";
      if (newsItem.customProperties) {
        let altCod = allWBSCustomProperties.find(
          (el) => el.data.label === alternativeCodeLabel
        );
        let descGroup = allWBSCustomProperties.find(
          (el) => el.data.label === resourceGroupLabel
        );
        customAlternativeCode = altCod
          ? newsItem.customProperties[altCod.id]
          : "";
        customDescriptionGroup = descGroup
          ? newsItem.customProperties[descGroup.id]
          : "";
      }
      const alternativeCode = newsItem.alternativeCodeItem
        ? newsItem.alternativeCodeItem
        : newsItem.alternativeCodeComposition
        ? newsItem.alternativeCodeComposition
        : customAlternativeCode;
      const DescriptionGroup = newsItem.descriptionGroup
        ? newsItem.descriptionGroup
        : newsItem.descriptionGroup
        ? newsItem.descriptionGroup
        : customDescriptionGroup;
      return (
        <TableRow>
          <TableCell className={classes.cellCenter} style={cellPaddingCorrect}>
            {newsItem.code}
          </TableCell>
          <TableCell className={classes.cellCenter} style={cellPaddingCorrect}>
            {alternativeCode}
          </TableCell>
          <TableCell style={cellPaddingCorrect}>
            {newsItem.description}
          </TableCell>
          <TableCell style={cellPaddingCorrect}>{DescriptionGroup}</TableCell>
          <TableCell className={classes.cellCenter} style={cellPaddingCorrect}>
            {newsItem.unit}
          </TableCell>
          <TableCell className={classes.cellCenter} style={cellPaddingCorrect}>
            {formatNumber(newsItem.quantity, 0, 4)}
          </TableCell>
          <TableCell className={classes.cellRight} style={cellPaddingCorrect}>
            {formatNumber(newsItem.unitcost)}
          </TableCell>
          <TableCell className={classes.cellRight} style={cellPaddingCorrect}>
            {formatNumber(newsItem.totalCost)}
          </TableCell>
        </TableRow>
      );
    });
  };

  if (showModal) {
    const _currentRow = WBSStore.WBSRows.find(
      (row) => row.data.contentId == rowDataShow.contentId
    );
    const currentRow = _currentRow
      ? _currentRow
      : { data: { rowCode: "0000", description: "", customProperties: {} } };
    let altCod = allWBSCustomProperties.find(
      (el) => el.data.label === alternativeCodeLabel
    );
    return (
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        open={showModal}
        onClose={() => setShowModal(false)}
        className={classes.dialogClass}
      >
        <DialogContent style={{ backgroundColor: "#fff", paddingTop: "5px" }}>
          {!loading ? (
            <>
              {dataNew.length > 0 && (
                <TableComposition
                  classes={classes}
                  isNew={true}
                  code={rowDataShow.code}
                  alternativeCodeComposition={
                    rowDataShow.alternativeCodeComposition
                  }
                  description={rowDataShow.description}
                >
                  {displayTableBody(dataNew)}
                </TableComposition>
              )}
              <TableComposition
                classes={classes}
                isNew={false}
                code={currentRow.data.rowCode}
                alternativeCodeComposition={
                  altCod &&
                  currentRow.data.customProperties &&
                  currentRow.data.customProperties[altCod.id]
                    ? currentRow.data.customProperties[altCod.id]
                    : undefined
                }
                description={currentRow.data.description}
              >
                {dataCurrent.length > 0 ? (
                  displayTableBody(dataCurrent)
                ) : (
                  <TableRow>
                    <TableCell className={classes.cellEmptyCenter} colSpan={6}>
                      {i18n.updateCompositions.emptyComposition}
                    </TableCell>
                  </TableRow>
                )}
              </TableComposition>
            </>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <CircularProgress style={{ color: "#9AAF00" }} />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)} color="primary" autoFocus>
            {i18n.updateCompositions.return}
          </Button>
        </DialogActions>
      </Dialog>
    );
  } else {
    return <></>;
  }
};

export default withStyles(styles)(DetailsModal);



// WEBPACK FOOTER //
// ./src/modules/WBS/components/layouts/DetailsModal.tsx